import { useQuery } from '@tanstack/react-query';
import yasml from '@thirtytech/yasml';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import {
  BrokerNerdsBarkerCoreModelsWarehouseListingSnapshot,
  getApiMarketplacesMarketplaceIdEventsEventIdSnapshots,
  getGetApiInventoryEventsEventIdMappingsQueryKey,
  putApiMarketplacesMarketplaceIdEventsEventIdSnapshotsFiltered,
  putApiMarketplacesMarketplaceIdEventsEventIdSnapshotsRecent,
  useGetApiInventoryEventsEventIdMappings,
} from '../../api';
import { useAtomValue } from 'jotai';
import { filtersEmptyAtom, ruleStateAtom, seatingChartFiltersAtom, selectedEventAtom } from '../../data/atoms';
import { useLocalStorage } from '@mantine/hooks';
import { ChartTimePeriod } from '../../components/Charts/TicketVolumeAndPrice';
import { useRuleState } from '../../data/Rule.state';

function MarketplaceTrendsState() {
  const selectedEvent = useAtomValue(selectedEventAtom);
  const ruleFiltersEmpty = useAtomValue(filtersEmptyAtom);
  const rule = useAtomValue(ruleStateAtom);

  const filters = useAtomValue(seatingChartFiltersAtom);
  const selectedMarketplaceId = rule.marketplaceId;
  const { isLoading: isRuleLoading } = useRuleState('isLoading', 'resetForm');
  const [snapshotPeriod, setSnapshotPeriod] = useLocalStorage<ChartTimePeriod>({
    key: 'setting-snapshotPeriod',
    defaultValue: '1w',
    getInitialValueInEffect: false,
    // serialize: (value) => value,
    // deserialize: (value) => value as ChartTimePeriod,
  });
  const { data: eventMappings, isFetching: isMappingsLoading } = useGetApiInventoryEventsEventIdMappings(selectedEvent?.eventId!, {
    query: {
      enabled: !!selectedEvent?.eventId,
      queryKey: [...getGetApiInventoryEventsEventIdMappingsQueryKey(selectedEvent?.eventId!), selectedEvent?.tenantId],
      select(data) {
        return data.data;
      },
    },
    axios: {
      headers: {
        'x-tenant-id': selectedEvent?.tenantId,
      },
    },
  });
  const mapping = useMemo(() => eventMappings?.find((m) => m.marketplaceId === selectedMarketplaceId), [eventMappings, selectedMarketplaceId]);
  const toDate = useMemo(() => dayjs().endOf('day').toDate(), []);
  const { data: snapshots, isFetching: isSnapshotsLoading } = useQuery({
    queryKey: [ruleFiltersEmpty, isRuleLoading, selectedMarketplaceId, mapping?.marketplaceEventId, snapshotPeriod, selectedEvent?.tenantId, filters],
    queryFn: () => {
      // Return a snapshot for a specific marketplace event and period; ensuring the marketplaceEventId is available.
      if (ruleFiltersEmpty && snapshotPeriod !== '1d' && mapping?.marketplaceEventId) {
        return getApiMarketplacesMarketplaceIdEventsEventIdSnapshots(selectedMarketplaceId, mapping.marketplaceEventId, {
          headers: {
            'x-tenant-id': selectedEvent?.tenantId,
          },
        });
      }
      if (!ruleFiltersEmpty && !isRuleLoading && selectedMarketplaceId && mapping?.marketplaceEventId) {
        if (snapshotPeriod === '1d') {
          return putApiMarketplacesMarketplaceIdEventsEventIdSnapshotsRecent(selectedMarketplaceId, mapping?.marketplaceEventId!, filters, {
            headers: {
              'x-tenant-id': selectedEvent?.tenantId,
            },
          });
        }

        return putApiMarketplacesMarketplaceIdEventsEventIdSnapshotsFiltered(
          selectedMarketplaceId,
          mapping?.marketplaceEventId!,
          filters,
          {
            from:
              snapshotPeriod === '1w'
                ? dayjs(toDate).subtract(1, 'week').startOf('day').toDate()
                : snapshotPeriod === '1m'
                  ? dayjs(toDate).subtract(1, 'month').startOf('day').toDate()
                  : snapshotPeriod === '3m'
                    ? dayjs(toDate).subtract(3, 'month').startOf('day').toDate()
                    : snapshotPeriod === '1y'
                      ? dayjs(toDate).subtract(1, 'year').startOf('day').toDate()
                      : dayjs(toDate).subtract(100, 'year').startOf('day').toDate(),
            to: toDate,
          },
          {
            headers: {
              'x-tenant-id': selectedEvent?.tenantId,
            },
          },
        );
      }
      return Promise.resolve({ data: [] as BrokerNerdsBarkerCoreModelsWarehouseListingSnapshot[] });
    },
  });

  const isLoading = useMemo(() => isMappingsLoading || isRuleLoading || isSnapshotsLoading, [isMappingsLoading, isRuleLoading, isSnapshotsLoading]);
  const [showMedPrice, setShowMedPrice] = useState(true);
  const [showMinPrice, setShowMinPrice] = useState(true);
  const [showVolume, setShowVolume] = useState(true);

  const data = useMemo(() => {
    if (!snapshots?.data) return [];
    // Hoist the period-dependent cutoff out of the per-element filter callback. snapshotPeriod is
    // constant for the whole pass, so only one of the original mutually-exclusive branches could ever
    // match; computing the single relevant cutoff once (and reading the clock once) preserves which
    // snapshots pass while avoiding per-element dayjs() allocations.
    const cutoff =
      snapshotPeriod === '1d'
        ? dayjs().subtract(1, 'day')
        : snapshotPeriod === '1w'
          ? dayjs().subtract(1, 'week')
          : snapshotPeriod === '1m'
            ? dayjs().subtract(1, 'month')
            : snapshotPeriod === '3m'
              ? dayjs().subtract(3, 'month')
              : snapshotPeriod === '1y'
                ? dayjs().subtract(1, 'year')
                : null; // 'all' (or any unknown period) keeps everything, matching the original `|| snapshotPeriod === 'all'`.
    // The map's format string only depends on snapshotPeriod, which is loop-invariant; resolve it once.
    const format = snapshotPeriod === '1d' ? 'h A' : 'M/D';
    return (
      snapshots.data
        .filter((snapshot) => cutoff === null || dayjs(snapshot.createdAt).isAfter(cutoff))
        // Precompute each createdAt timestamp once and compare numerically. The original comparator
        // returned 1 when a is strictly after b and -1 otherwise (never 0); `aTime > bTime ? 1 : -1`
        // reproduces that exact result while avoiding O(n log n) dayjs constructions in the comparator.
        .map((snapshot) => ({ snapshot, time: dayjs(snapshot.createdAt).valueOf() }))
        .sort((a, b) => (a.time > b.time ? 1 : -1))
        .map(({ snapshot }) => ({
          name: dayjs(snapshot.createdAt).format(format),
          tickets: snapshot.totalTickets,
          medPrice: snapshot.medPrice,
          minPrice: snapshot.minPrice,
        }))
    );
  }, [snapshots?.data, snapshotPeriod]);

  return {
    isLoading,
    snapshots,
    snapshotPeriod,
    setSnapshotPeriod,
    showMedPrice,
    setShowMedPrice,
    showMinPrice,
    setShowMinPrice,
    showVolume,
    setShowVolume,
    data,
  };
}

export const { Provider: MarketplaceTrendsProvider, useSelector: useMarketplaceTrends } = yasml(MarketplaceTrendsState);
